/*
    This library requires Secure LS library
    install: npm install secure-ls
    site: https://github.com/softvar/secure-ls
 */

import {b64Encode} from './Common';
import {Storage} from './Storage';
import {updateBearerToken} from './Sockets'

import SecureLS from 'secure-ls'

const CURRENT_DOMAIN = window.location.host.split(':')[0]
const BASE_API_URL = process.env.VUE_APP_PUBLIC_PROFILE_DOMAIN.match(CURRENT_DOMAIN) ?
    process.env.VUE_APP_PUBLIC_PROFILE_API_URL : process.env.VUE_APP_API_URL;

const TIMEOUT = 80000;

const POST = 'POST';
const GET = 'GET';
const PUT = 'PUT';
const PATCH = 'PATCH';
const DELETE = 'DELETE';

const UNAUTHORIZED = 401;
const NOT_FOUND = 404;
const SUCCESS = 200;
const CREATED = 201;
const TOKEN_EXPIRED = 'token_expired'

var tries = 0;

import store from '@/store'
import router from '@/router'

export const ApiUtils = {

    apiEndpoints: {
        login: 'auth/login',
        clientLogin: 'auth/login/client',
        loginSocial: 'auth/social/%provider%',
        refreshToken: 'auth/token/refresh',
        passwordReset: 'password/reset',
        passwordResetEmail: 'password/reset/email',
        me: 'me?institutions&ratings'
    },

    jwt: null,

    flushData: async (flushStoredCredentials = false) => {

        store.commit('setCurrentUser', null)
        Storage.setUser(null)
        await ApiUtils.setJWT()

        if(flushStoredCredentials){
            await ApiUtils.setCredentials(null)
        }

    },

    logout: async (redirect = true) => {
        console.log('logout...', router.currentRoute)
        if(!router.currentRoute.meta || router.currentRoute.meta.public !== true){
            store.dispatch('resetAll')
            store.commit('setBackToEvent', {event: null, show: false})
            router.replace({name: 'home'})
        }
        await ApiUtils.flushData();
        store.commit('blurWrapper', false)
    },

    loginWithStoredCredentials: async () => {

        let credentials = await ApiUtils.getUserCredentials();
        if(!Object.keys(credentials).length){
            throw Error('Credentials not set');
        }

        let loginResponse = await ApiUtils.login(credentials.username, credentials.password, true);
        if(loginResponse.code !== 200){
            throw Error('Login error: ', loginResponse.message);
        }

    },

    getUserCredentials: async ()=>{
        var ls = new SecureLS();
        return ls.get('credentials')
    },

    setCredentials: async function(credentials = {}){
        try {
            var ls = new SecureLS();
            ls.set('credentials', credentials);
        } catch (error) {
           throw new Error('Error trying to save Credentials: ' + error)
        }
    },

    setJWT: async function(token = null, expiresIn = 7200){
        try {
            var now = Math.floor(Date.now() / 1000)
            const jwt = {
                access_token: token,
                expires_in: expiresIn,
                created_at: now,
                is_expired: (Number(now) + Number(expiresIn)) < now
            }

            var ls = new SecureLS();
            ls.set('jwt', jwt);

        } catch (error) {
           throw new Error('Error trying to save JWT')
        }
    },

    getJWT: function(){

        try{
            var ls = new SecureLS();
            var jwt = ls.get('jwt')

            if(typeof jwt === 'string'){
                jwt = {
                    access_token: null,
                    expires_in: 0,
                    created_at: 0
                }
            }

            jwt.is_expired = (Number(jwt.created_at) + Number(jwt.expires_in)) < Math.floor(Date.now() / 1000)

            return jwt

        }catch(err){

             var jwt = {
                    access_token: null,
                    expires_in: 0,
                    created_at: 0
                }

            jwt.is_expired = (Number(jwt.created_at) + Number(jwt.expires_in)) < Math.floor(Date.now() / 1000)

            return jwt
        }

    },

    login: async (email, password, remember = false) => {
        return await ApiUtils.requestNewAccessToken({email, password}, null, remember)
    },

    clientLogin: async (dni, remember = false) => {
        return await ApiUtils.requestNewAccessToken({dni}, null, remember)
    },

    loginWithSocial: async (accessToken = '', provider = 'facebook') => {
        return await ApiUtils.requestNewAccessToken(null, {provider, accessToken})
    },

    passwordResetEmail: async (email) => {
        return await ApiUtils.makePost(ApiUtils.apiEndpoints.passwordResetEmail, {email})
    },

    passwordReset: async (token, password) => {
        return await ApiUtils.makePost(ApiUtils.apiEndpoints.passwordReset, {token, password, password_confirmation: password})
    },

    refreshToken: async () => {



        var endpoint = BASE_API_URL + ApiUtils.apiEndpoints.refreshToken
        var jwt = ApiUtils.getJWT()
        if(!jwt.access_token){
            throw Error('error trying to refresh token: no token set')
        }

        const headers = {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + jwt.access_token,
            'Cache-Control': 'no-cache'
        };

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: headers
        })

        if(!response.ok){
            await ApiUtils.logout()

        }

        const json = await response.json()
        await ApiUtils.setJWT(json.result.access_token, json.result.expires_in)
        updateBearerToken(json.result.access_token)

        return json.result

    },
    requestNewAccessToken: async (credentials = {}, social = {}, remember = false) => {

        var body = {}
        var endpoint

        if(credentials && credentials.email && credentials.password){
            endpoint = ApiUtils.apiEndpoints.login
            body.email = credentials.email
            body.password = credentials.password
        }else if(credentials && credentials.dni){
            endpoint = ApiUtils.apiEndpoints.clientLogin
            body.dni = credentials.dni
        }else if(social && social.provider && social.accessToken){
            endpoint = ApiUtils.apiEndpoints.loginSocial.replace(/%\w+%/g, social.provider)
            body.accessToken = social.accessToken
        }else{
            throw Error('Can\'t request token: No credentials nor social access set')
        }

        store.commit('setLoginLoading', true)
        const response = await ApiUtils.makePost(endpoint, body)
        const json = await response.json();
        await ApiUtils.updateAccessData(json, remember)

        if(remember){
            ApiUtils.setCredentials(credentials)
        }

        store.commit('setLoginLoading', false)
        return json;

    },
    updateAccessData: async (response, remember = false) => {

        if(response.code == 200){

            await ApiUtils.setJWT(response.result.access_token, response.result.expires_in)
            updateBearerToken(response.result.access_token)
            console.log('updating access data')
            await ApiUtils.fetchMe()
        }
    },
    fetchMe: async () => {
        const jwt = ApiUtils.getJWT()
        if(
            !jwt.access_token &&
            window.location.pathname !== '/' &&
            (!router.currentRoute.meta || router.currentRoute.meta.public !== true))
        {
            window.location.href = '/'
        }
        const meResponse = await ApiUtils.makeGet(ApiUtils.apiEndpoints.me)
        let me = null
        if(meResponse.ok){
            const meJson = await meResponse.json()
            me = meJson.result.me
        }else{
            let meJson = await meResponse.json()
            console.log('me response failed', meJson)
        }

        console.log('set me:', me)
        Storage.setUser(me);
        store.commit('setCurrentUser', me);

    },
    makeGet: async (endpoint, data = {})=>{

        let queryString = '';

        if(Object.keys(data).length > 0){
            queryString = '?' + ApiUtils.objectToQueryString(data)
        }

        let uri = typeof ApiUtils.apiEndpoints[endpoint] != 'undefined' ?
            ApiUtils.apiEndpoints[endpoint] : endpoint;

        return await ApiUtils.makeRequest(uri + queryString, GET);

    },
    objectToQueryString: (obj, queryString = '', prepend = '') => {

        for (var i in obj) {

            if (Array.isArray(obj[i]) || typeof (obj[i]) === 'object') {
                ;
                prepend = prepend ? `${prepend}[${i}]` : `${i}`
                queryString = ApiUtils.objectToQueryString(obj[i], queryString, prepend);
                prepend = ''
                continue;
            }

            var attr = prepend ? `${prepend}[${i}]` : i;
            queryString += `${attr}=${obj[i]}&`;

        }

        return  queryString

    },
    _handleError: async (response, url, method, headers, data) => {
        ;
        return response;
    },
    timedOutFetch(ms, promise) {
        return new Promise(function(resolve, reject) {
            setTimeout(function() {
                reject(new Error('fetch timeout reached'))
            }, ms)
            promise.then(resolve, reject)
        })
    },
    makeRequest: async (uri, method = POST, data = {})=>{

        const regex = new RegExp('https?')
        var url = regex.test(uri) ? uri : BASE_API_URL + uri
        url = url.replace(/\/$/, '') //remove trailing slash

        var jwt = ApiUtils.getJWT();
        var currentUser = Storage.getUser()



        if(jwt.access_token && jwt.is_expired && currentUser){
            jwt = await ApiUtils.refreshToken()
        }

        const headers = {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + jwt.access_token,
            'Cache-Control': 'no-cache'
        };

        let response = null;
        let fetchOptions = {
            method: method === PATCH ||  method === DELETE ? POST : method,
            headers: headers
        }

        if(method === GET){
            response = await ApiUtils.timedOutFetch(TIMEOUT, fetch(url, fetchOptions));
        }else{

             var formData = Object.keys(data).length > 0 ? ApiUtils.generateFormData(data) : new FormData()

            if(method === PATCH){
                formData.append('_method', PATCH)
            }

            if(method === DELETE){
                formData.append('_method', DELETE)
            }

            fetchOptions.body = formData


            response = await ApiUtils.timedOutFetch(TIMEOUT, fetch(url, fetchOptions));
        }

        if(response.ok){
            return response;
        }

        if(jwt !== '' && response.status === UNAUTHORIZED){
            try{
                ApiUtils.setCurrentUser(null)
                store.commit('openLoginForm', true)
            }catch(error){
                return ApiUtils._handleError(response, url, method, headers, data);
            }

        }

        return ApiUtils._handleError(response, url, method, headers, data);



    },

    makePost: async (endpoint, data = {})=>{

        let uri = typeof ApiUtils.apiEndpoints[endpoint] != 'undefined' ?
            ApiUtils.apiEndpoints[endpoint] : endpoint;

        const response = await ApiUtils.makeRequest(uri, POST, data);

        return response;

    },

    makePatch: async (endpoint, id, data = {})=>{

        let uri = typeof ApiUtils.apiEndpoints[endpoint] != 'undefined' ?
            ApiUtils.apiEndpoints[endpoint] : endpoint;

        let url = uri;

        if(typeof id == 'number' || typeof id == 'integer'){
            url += '/' + id;
        }

        const response = await ApiUtils.makeRequest(url, PATCH, data);

        return response;

    },

    makeDelete: async (endpoint, id, data = {})=>{

        let uri = typeof ApiUtils.apiEndpoints[endpoint] != 'undefined' ?
            ApiUtils.apiEndpoints[endpoint] : endpoint;

        let url = uri;

        if(typeof id == 'number' || typeof id == 'integer'){
            url += '/' + id;
            // return;
        }

        return await ApiUtils.makeRequest(url, DELETE, data);

    },

    generateFormData: (data = {}, formData = null, prefixes = []) => {

        if(formData == null){
            formData = new FormData()
        }

        for(let i in data){

            if(!(data[i] instanceof File) && typeof data[i] === 'object'){
                prefixes.push(i)
                ApiUtils.generateFormData(data[i], formData, prefixes.slice())
                prefixes.pop()
                continue;
            }

            let attribute = prefixes[0] || ''
            let content = data[i]

            for(let j in prefixes){
                if(j == 0) continue;
                attribute += '['+ prefixes[j] +']'
            }

            attribute += attribute == '' ? i : '[' + i + ']'

            formData.append(attribute, content)

        }

        return formData

    }

}
